/**
 * What a job's budget numbers mean, and how to write them out.
 *
 * budget_min and budget_max are yearly amounts; the currency says how to read
 * them. Every screen used to spell this out for itself as rupees, so a dollar
 * role was quoted as "₹90,000 LPA". This is the one place that decides, and it
 * mirrors backend/core/budget.py.
 */

export const CURRENCIES = [
  { code: 'INR', symbol: '₹', label: 'INR (₹)' },
  { code: 'USD', symbol: '$', label: 'USD ($)' },
];

export const DEFAULT_CURRENCY = 'INR';
const PERIOD = 'per year';

export function currencySymbol(code) {
  return (CURRENCIES.find(c => c.code === code) || CURRENCIES[0]).symbol;
}

/** What a number of this size usually looks like, as a hint in an empty box. */
export function budgetPlaceholder(currency, bound) {
  const pair = currency === 'USD' ? ['90000', '140000'] : ['1200000', '2000000'];
  return `e.g. ${bound === 'Min' ? pair[0] : pair[1]}`;
}

/**
 * The hint in a candidate's current / expected salary box.
 *
 * A role quoted in dollars should not ask the applicant for a figure in
 * rupees: the hint follows the job, so the number they type means what the
 * recruiter reads it as.
 */
export function ctcPlaceholder(currency, kind) {
  const pair = currency === 'USD' ? ['$90,000', '$120,000'] : ['₹8,00,000', '₹11,00,000'];
  return `e.g. ${kind === 'expected' ? pair[1] : pair[0]}`;
}

/** A budget as a person reads it: "₹12,00,000 – ₹20,00,000 per year". */
export function formatBudget(job, empty = '') {
  if (!job) return empty;
  const sym = currencySymbol(job.budget_currency);
  // Rupees group the Indian way (12,00,000), dollars the other (1,200,000).
  const locale = job.budget_currency === 'USD' ? 'en-US' : 'en-IN';
  const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && v !== null && v !== '' ? `${sym}${n.toLocaleString(locale)}` : '';
  };
  const lo = num(job.budget_min);
  const hi = num(job.budget_max);
  if (lo && hi) return `${lo} – ${hi} ${PERIOD}`;
  if (lo) return `From ${lo} ${PERIOD}`;
  if (hi) return `Up to ${hi} ${PERIOD}`;
  return empty;
}
